import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { PageHead } from "@/components/PageHead";
import {
  fetchHomePageContent,
  getCachedHomePageContent,
  primeHomePageCache,
  type HomePageContent,
  type HomePageSection,
} from "@/lib/homepage";
import { PrerenderData, readPrerenderJson } from "@/lib/prerenderData";
import { orderSections, renderSection } from "@/components/FlexSectionRenderer";

const DEFAULT_TITLE = "OnPlexus: Global Currency Orchestration Platform";

function markPrerenderReady() {
  if (typeof window !== "undefined") {
    (window as any).__PRERENDER_READY__ = true;
  }
}


type PreviewInfo = { id: number; nonce: string; lang?: string } | null;

function parsePreview(search: string): PreviewInfo {
  const params = new URLSearchParams(search);
  const isPreview = params.get("preview") === "true" || params.get("preview") === "1";
  const idParam = params.get("preview_id") ?? params.get("p");
  const nonce = params.get("preview_nonce") ?? params.get("_wpnonce");
  const lang = params.get("lang")?.toLowerCase() ?? undefined;

  if (!isPreview && !idParam) return null;
  const id = idParam ? Number(idParam) : NaN;
  if (!id || Number.isNaN(id) || !nonce) return null;
  return { id, nonce, lang };
}

type IndexProps = {
  lang?: string;
};

export default function Index({ lang }: IndexProps = {}) {
  const location = useLocation();

  const preview = useMemo(() => parsePreview(location.search), [location.search]);
  const dataId = useMemo(() => (lang ? `__PR_HOME_${lang.toUpperCase()}` : "__PR_HOME__"), [lang]);

  const inline = useMemo(() => {
    if (typeof window === "undefined" || preview) return null;
    return readPrerenderJson<HomePageContent>(dataId);
  }, [preview, dataId]);

  const cached = useMemo(() => {
    if (typeof window === "undefined" || preview) return null;
    return getCachedHomePageContent(lang);
  }, [preview, lang]);

  const initialContent = inline ?? cached ?? null;

  const [content, setContent] = useState<HomePageContent | null>(initialContent);
  const [loading, setLoading] = useState(!initialContent);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (inline && !preview) {
      primeHomePageCache(lang, inline);
    }
  }, [inline, lang, preview]);

  useEffect(() => {
    if (initialContent) {
      setContent(initialContent);
      setLoading(false);
      setError(null);
    }
  }, [initialContent]);

  useEffect(() => {
    if (inline) {
      markPrerenderReady();
      return;
    }

    if (cached) {
      markPrerenderReady();
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setContent(null);

    fetchHomePageContent(
      preview
        ? {
            previewId: preview.id,
            previewNonce: preview.nonce,
            previewLang: preview.lang ?? lang,
          }
        : { lang }
    )
      .then((data) => {
        if (cancelled) return;
        if (!data) {
          setError("Homepage content not found");
          setContent(null);
          markPrerenderReady();
          return;
        }
        if (!preview) {
          primeHomePageCache(lang, data);
        }
        setContent(data);
        setError(null);
        markPrerenderReady();
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setContent(null);
        markPrerenderReady();
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [inline, cached, preview, lang]);

  const sections = orderSections(content?.sections ?? []);
  const hasSections = sections.length > 0;
  const pageTitle = content?.seo?.title || content?.title || DEFAULT_TITLE;
  const pageDescription = content?.seo?.description || undefined;
  const canonicalPath = lang === "fr" ? "/fr" : "/";
  const alternateRoute = content?.alternateRoute ?? null;
  const alternates = lang === "fr"
    ? [
        { hrefLang: "en", href: alternateRoute ?? "/" },
        { hrefLang: "fr", href: canonicalPath },
        { hrefLang: "x-default", href: alternateRoute ?? "/" },
      ]
    : [
        { hrefLang: "en", href: canonicalPath },
        { hrefLang: "fr", href: alternateRoute ?? "/fr" },
        { hrefLang: "x-default", href: canonicalPath },
      ];

  return (
    <div className="min-h-screen bg-onplexus-dark">
      <PageHead
        title={pageTitle}
        description={pageDescription}
        canonical={canonicalPath}
        alternates={alternates}
        seo={content?.seo}
      />
      <Header />
      <main>
        {/* Always render PrerenderData when we have content, even if it has no sections */}
        {!preview && content && (
          <PrerenderData id={dataId} value={content} />
        )}

        {loading && (
          <div className="py-24 text-center text-white/70">Loading homepage…</div>
        )}

        {!loading && error && (
          <div className="py-24 text-center text-red-300">{error}</div>
        )}

        {!loading && !error && !hasSections && content && (
          <div className="py-24 text-center text-white/70">
            <p>Page loaded but no content sections found.</p>
            <p className="text-sm mt-2">Page ID: {content.id}</p>
          </div>
        )}

        {!loading && !error && hasSections && (
          sections.map((section, index) => renderSection(section, index))
        )}
      </main>
      <Footer />
    </div>
  );
}
